/**
 * ProjectTopNav — Sticky Sub-Navigation für alle Projekt-Unterseiten.
 *
 * Verwendet die zentrale `PROJECT_PAGES` Konfiguration (config/projectNav.js)
 * als Single Source of Truth — Label-Änderungen schlagen automatisch hier UND
 * in der Layout-ProjectSubNav durch.
 *
 * Optional: showTitle=true zeigt zusätzlich den Projekt-Namen-Header & aktuelle
 * Seiten-Bezeichnung an (über der Tab-Liste).
 */
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../App";
import {
  PROJECT_PAGES,
  getVisibleProjectPages,
  buildProjectPageUrl,
  getProjectPageFromPath,
} from "../config/projectNav";

export default function ProjectTopNav({
  projectId: projectIdProp,
  projectName = null,
  taskSummary = null,
  reportsCount = 0,
  showTitle = false,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const projectId = projectIdProp || params.projectId;

  const visiblePages = getVisibleProjectPages(user?.role).filter(
    (p) => p.key !== "overview" // wird via ArrowLeft / Zurück-Button gezeigt
  );
  const currentPage = getProjectPageFromPath(location.pathname);

  // Counts (Aufgaben offen, Berichte gesamt)
  const counts = {
    tasks: {
      open: (taskSummary?.by_status?.offen || 0) + (taskSummary?.by_status?.in_arbeit || 0),
      total: taskSummary?.total || 0,
    },
    dailyReports: { total: reportsCount },
  };

  const isActive = (p) => {
    if (p.absoluteRoute) return location.pathname === p.absoluteRoute;
    return location.pathname.includes(`/projekte/${projectId}/${p.route}`);
  };

  const overviewUrl = buildProjectPageUrl("overview", projectId);
  const onOverview = location.pathname.includes(`/projekte/${projectId}/${PROJECT_PAGES.overview.route}`);

  return (
    <div
      className="project-top-nav-sticky"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40, // unter Layout-Header (50)
        background: "rgba(10, 18, 32, 0.78)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,0.10)",
        marginLeft: "-16px",
        marginRight: "-16px",
        paddingLeft: "16px",
        paddingRight: "16px",
        marginBottom: "12px",
      }}
      data-testid="project-top-nav"
    >
      {showTitle && currentPage && (
        <div className="pt-3 pb-1.5 border-b border-white/5 mb-1">
          {projectName && (
            <div className="text-[11px] uppercase tracking-wider text-blue-300/70 font-semibold">
              {projectName}
            </div>
          )}
          <div className="text-lg font-bold text-white" data-testid="project-page-title">
            {currentPage.title}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 py-2 overflow-x-auto">
        {/* Zurück zur Projektübersicht (überall außer auf der Übersicht selbst) */}
        {!onOverview && (
          <button
            onClick={() => navigate(overviewUrl)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 mr-1 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 border"
            style={{
              color: "rgba(226, 232, 240, 0.85)",
              borderColor: "rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#60a5fa";
              e.currentTarget.style.background = "rgba(59,130,246,0.12)";
              e.currentTarget.style.borderColor = "rgba(96,165,250,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "rgba(226, 232, 240, 0.85)";
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
            }}
            title="Zurück zur Projektübersicht"
            data-testid="top-nav-back-to-overview"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{PROJECT_PAGES.overview.label}</span>
          </button>
        )}

        {visiblePages.map((p) => {
          const active = isActive(p);
          const url = buildProjectPageUrl(p.key, projectId);
          const Icon = p.icon;
          const cnt = counts[p.key];
          const showOpenBadge = p.key === "tasks" && cnt?.open > 0;
          const showTotalBadge = !showOpenBadge && cnt?.total > 0;

          return (
            <button
              key={p.key}
              onClick={() => navigate(url)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0"
              style={{
                color: active ? "#60a5fa" : "rgba(226, 232, 240, 0.85)",
                backgroundColor: active ? "rgba(59,130,246,0.16)" : "transparent",
                border: active ? "1px solid rgba(96,165,250,0.35)" : "1px solid transparent",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.color = "#60a5fa";
                  e.currentTarget.style.backgroundColor = "rgba(59,130,246,0.10)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.color = "rgba(226, 232, 240, 0.85)";
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
              data-testid={`top-nav-${p.testKey}`}
            >
              <Icon className="w-4 h-4" />
              {p.label}
              {showOpenBadge && (
                <span
                  className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full"
                  style={{ background: "rgba(59,130,246,0.18)", color: "#93c5fd" }}
                >
                  {cnt.open}
                </span>
              )}
              {showTotalBadge && (
                <span
                  className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full"
                  style={{ background: "rgba(255,255,255,0.08)", color: "rgba(226,232,240,0.7)" }}
                >
                  {cnt.total}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
